import React, { useState, useEffect, useRef } from 'react'
import { Label, TextInput, Button, HelperText } from 'flowbite-react'
import ReactSelect from 'react-select'

// your existing styles for ReactSelect
const customStyles = {
  control: (base) => ({
    ...base,
    boxShadow: 'none',
    borderColor: '#ccc',
    '&:hover': { borderColor: '#bbb' },
  }),
  input: (base) => ({ ...base, outline: 'none' }),
}

// static options for hardcoded vs dynamic
const modeOptions = [
  { value: 'hardcoded', label: 'Hardcoded String' },
  { value: 'dynamic',  label: 'Request Body ( Field -> Subfield )' },
  { value: 'currentDate', label: 'Current Date' },
]

const dateFormatOptions = [
  { value: 'mm-dd-yyyy', label: 'MM-DD-YYYY (09-23-2025)' },
  { value: 'dd-mm-yyyy', label: 'DD-MM-YYYY (23-09-2025)' },
  { value: 'yyyy-mm-dd', label: 'YYYY-MM-DD (2025-09-23)' },
  { value: 'mm/dd/yyyy', label: 'MM/DD/YYYY (09/23/2025)' },
  { value: 'dd/mm/yyyy', label: 'DD/MM/YYYY (23/09/2025)' },
  { value: 'yyyy/mm/dd', label: 'YYYY/MM/DD (2025/09/23)' },
]

export default function FieldSelector({
  index,
  data,
  onChange,     // (index, key, value) => void
  onRemove,     // (index) => void
  fieldsMap     // { [fieldName]: any }
}) {
  const hcRef = useRef(null)

  // ─── local state ────────────────────────────────────────────────────────────
  const [mode,          setMode]          = useState(data.mode          || 'hardcoded')
  const [hcValue,       setHcValue]       = useState(data.value         || '')
  const [fieldValue,    setFieldValue]    = useState(data.field         || '')
  const [subfieldValue, setSubfieldValue] = useState(data.subfield      || '')
  const [subSubValue,   setSubSubValue]   = useState(data.subSubfield   || '')
  const [subSubSubValue,setSubSubSubValue]= useState(data.subSubSubfield|| '')
  const [dateFormat,    setDateFormat]    = useState(data.dateFormat    || 'mm-dd-yyyy')

  // ─── handlers ───────────────────────────────────────────────────────────────

  const handleModeChange = (opt) => {
    const v = opt?.value || 'hardcoded'
    setMode(v)
    setFieldValue('')
    setSubfieldValue('')
    setSubSubValue('')
    setSubSubSubValue('')
    setHcValue('')
    onChange(index, 'mode', v)
  }

  const handleDateFormatChange = (opt) => {
    const v = opt?.value || 'mm-dd-yyyy'
    setDateFormat(v)
    onChange(index, 'dateFormat', v)
  }

  const handleHcChange = (e) => {
    const v = e.target.value
    setHcValue(v)
    onChange(index, 'value', v)
  }
  const handleFieldChange = (opt) => {
    const v = opt?.value || ''
    setFieldValue(v)
    onChange(index, 'field', v)
    // clear deeper selections
    setSubfieldValue('')
    onChange(index, 'subfield', '')
    setSubSubValue('')
    onChange(index, 'subSubfield', '')
    setSubSubSubValue('')
    onChange(index, 'subSubSubfield', '')
  }
  const handleSubfieldChange = (opt) => {
    const v = opt?.value || ''
    setSubfieldValue(v)
    onChange(index, 'subfield', v)
    // clear sub-sub
    setSubSubValue('')
    onChange(index, 'subSubfield', '')
  }
  const handleSubSubChange = (opt) => {
    const v = opt?.value || ''
    setSubSubValue(v)
    onChange(index, 'subSubfield', v)
    // clear sub-sub-sub
    setSubSubSubValue('')
    onChange(index, 'subSubSubfield', '')
  }
  const handleSubSubSubChange = (opt) => {
    const v = opt?.value || ''
    setSubSubSubValue(v)
    onChange(index, 'subSubSubfield', v)

  }

  // ─── build select options ────────────────────────────────────────────────────
  const fieldOptions = Object.keys(fieldsMap || {}).map(f => ({ value: f, label: f }))
  const subfieldOptions = fieldValue
    ? Object.keys(fieldsMap[fieldValue]?.subfields || {}).map(sf => ({ value: sf, label: sf }))
    : []

  const selectedType = fieldsMap[fieldValue]?.subfields?.[subfieldValue]
  const subSubOptions = selectedType && typeof selectedType === 'object'
    ? Object.keys(selectedType).map(ssf => ({ value: ssf, label: ssf }))
    : []
  const subSubSubOptions = selectedType && subSubValue && typeof selectedType[subSubValue] === 'object'
    ? Object.keys(selectedType[subSubValue]).map(sssf => ({ value: sssf, label: sssf }))
    : []

  const isList = fieldsMap[fieldValue]?.isList || false;

  // ─── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 border border-gray-300 p-4 rounded-md mb-4">
      {/* Mode selector */}
      <div>
        <Label htmlFor={`mode-${index}`} className="mb-1">Value Source</Label>
        <ReactSelect
          inputId={`mode-${index}`}
          options={modeOptions}
          value={modeOptions.find(o => o.value === mode)}
          onChange={handleModeChange}
          styles={customStyles}
          isSearchable
        />
      </div>

      {/* Hardcoded input */}
      {mode === 'hardcoded' && (
        <div>
          <Label htmlFor={`hc-${index}`} className="mb-1">Enter text</Label>
          <TextInput
            id={`hc-${index}`}
            ref={hcRef}
            value={hcValue}
            onChange={handleHcChange}
            placeholder="Type here…"
          />
        </div>
      )}

      {/* Current Date input */}
      {mode === 'currentDate' && (
        <div>
          <Label htmlFor={`dateFormat-${index}`} className="mb-1">Select date format</Label>
          <ReactSelect
            inputId={`dateFormat-${index}`}
            options={dateFormatOptions}
            value={dateFormatOptions.find(o => o.value === dateFormat)}
            onChange={handleDateFormatChange}
            styles={customStyles}
            isSearchable
          />
        </div>
      )}

      {/* Dynamic inputs */}
      {mode === 'dynamic' && (
        <>
          {/* Field */}
          <div>
            <Label htmlFor={`field-${index}`} className="mb-1">Choose field</Label>
            <ReactSelect
              inputId={`field-${index}`}
              options={fieldOptions}
              value={fieldOptions.find(o => o.value === fieldValue) || null}
              onChange={handleFieldChange}
              styles={customStyles}
              isSearchable
            />
          </div>

          {/* Subfield */}
          <div>
            <Label htmlFor={`subfield-${index}`} className="mb-1">Choose subfield</Label>
            <ReactSelect
              inputId={`subfield-${index}`}
              options={subfieldOptions}
              value={subfieldOptions.find(o => o.value === subfieldValue) || null}
              onChange={handleSubfieldChange}
              styles={customStyles}
              isSearchable
              isDisabled={!fieldValue}
            />
          </div>

          {/* Sub-Subfield */}
          {subSubOptions.length > 0 && (
            <div>
              <Label htmlFor={`subsubfield-${index}`} className="mb-1">Choose nested field</Label>
              <ReactSelect
                inputId={`subsubfield-${index}`}
                options={subSubOptions}
                value={subSubOptions.find(o => o.value === subSubValue) || null}
                onChange={handleSubSubChange}
                styles={customStyles}
                isSearchable
                isDisabled={!subfieldValue}
              />
            </div>
          )}

          {/* Sub-Sub-Subfield */}
          {subSubSubOptions.length > 0 && (
            <div>
              <Label htmlFor={`subsubsubfield-${index}`} className="mb-1">Choose deeply nested field</Label>
              <ReactSelect
                inputId={`subsubsubfield-${index}`}
                options={subSubSubOptions}
                value={subSubSubOptions.find(o => o.value === subSubSubValue) || null}
                onChange={handleSubSubSubChange}
                styles={customStyles}
                isSearchable
                isDisabled={!subfieldValue}
              />
            </div>
          )}

          {/* List index input */}
          {isList && (
            <div>
              <Label htmlFor={`hc-${index}`} className="mb-1">
                Which item in the list would you like to select?
              </Label>

              <TextInput
                as="input"
                type="number"
                id={`hc-${index}`}
                ref={hcRef}
                value={hcValue}
                onChange={handleHcChange}
                placeholder={`Enter a number`}
                min={1}
              />

              <HelperText className="mt-1">
                Enter 1 for the 1st item, 2 for the 2nd, and so on.
              </HelperText>
            </div>
          )}
        </>
      )}

      {/* Remove */}
      <Button color="light" size="sm" onClick={() => onRemove(index)}>
        Remove
      </Button>
    </div>
  )
}
